import { SepaTransactionOutput } from '@allocab/allocab-invoice-service-types';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TransactionInvoicesListModal } from './TransactionInvoicesListModal';
import { TransactionStateBadge } from './TransactionStateBadge';
import { Link } from '../../components/atomic/Link';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { TRANSACTION_LIMIT, useFetchTransactions } from '../../hooks/useFetchTransactions';
import { queryKeys } from '../../services/datastore/queryKeys';
import { queryClient } from '../../services/datastore/react-query';
import { t } from '../../services/translation/translation';
import { formatDate } from '../../utils/date';
import { formatPrice } from '../../utils/price';

type Props = {
  status: 'REJECTED' | 'IN_PROCESS' | 'VALIDATED';
};

export const TransactionList = ({ status }: Props) => {
  const columns: TableColumn<SepaTransactionOutput>[] = [
    {
      name: t('common.date'),
      render: transaction => (
        <Link
          to={`/invoicing/transactions/${transaction.id}`}
          className="text-gray-900 dark:text-white"
          onClick={() => {
            queryClient.setQueryData(queryKeys.user.fetchTransaction(transaction.id), transaction);
          }}
        >
          <div className="inline-flex flex-col items-start">
            <span className="text-sm opacity-50">
              {transaction.status === 'VALIDATED'
                ? t('transaction.date.done', { date: '' })
                : transaction.status === 'REJECTED'
                ? t('transaction.date.cancelled', { date: '' })
                : ''}
            </span>
            <span className="text-sm">{formatDate(transaction.updatedAt, t('date.date-longer'))}</span>
          </div>
        </Link>
      ),
    },
    {
      name: t('common.status'),
      render: transaction => <TransactionStateBadge status={transaction.status} />,
    },
    {
      name: t('common.amount'),
      render: transaction => (
        <span className={`font-bold text-sm ${transaction.status !== 'REJECTED' ? 'text-green-500' : 'text-gray-800'}`}>
          {formatPrice(transaction.amount)}
        </span>
      ),
      sortKey: 'amount',
    },
    {
      name: t('common.invoices'),
      render: transaction => (
        <div>
          {transaction.invoicesNumber && transaction.invoicesNumber.length > 0 ? (
            <div
              onClick={() => setSelectedTransaction(transaction.invoicesNumber || [])}
              className="text-sm text-blue-500 cursor-pointer"
            >
              {t('transaction.invoices-list')}
            </div>
          ) : (
            '--'
          )}
        </div>
      ),
    },
  ];

  const [searchParams] = useSearchParams();

  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const { data, isLoading, isFetching, isPreviousData } = useFetchTransactions({ status }, currentPage);
  const nbPages = Math.ceil((data?.totalItems ?? 0) / TRANSACTION_LIMIT);

  const [selectedTransaction, setSelectedTransaction] = useState<string[]>([]);

  return (
    <div>
      <Table
        columns={columns}
        data={data?.items}
        identifier="id"
        isLoading={isLoading}
        isFetching={isPreviousData && isFetching}
        nbPages={nbPages}
        context={undefined}
        totalItems={data?.totalItems ?? 0}
      />
      {selectedTransaction.length > 0 ? (
        <TransactionInvoicesListModal
          onClose={() => setSelectedTransaction([])}
          selectedTransaction={selectedTransaction}
        />
      ) : null}
    </div>
  );
};
